import { format } from 'date-fns';
import { useToggle } from '@sdk/common/hooks/useToggle';
import AttachmentsPreview from '@sdk/attachments/components/AttachmentsPreview/AttachmentsPreview';
import { MediaEmbed } from '@sdk/components/messages/MediaEmbed/MediaEmbed';
import { useSession } from '@sdk/session/SessionContext';
import dateToTimeDay from '@sdk/utils/dateFormat/dateToTimeDay';

import { useConversationState } from '@web/conversations/Conversation/ConversationProvider/ConversationProvider';
import { MessagePost as MessagePostType } from '@web/conversations/types/Message';
import { MessageContent } from '@web/conversations/Message/MessageContent/MessageContent';
import { MessageContextMenu } from '@web/conversations/Message/MessageContextMenu/MessageContextMenu';
import { MessageAuthor } from '@web/conversations/Message/MessageAuthor/MessageAuthor';
import {
  MessageContextMenuWrapper,
  MessageLayout,
  MessageSidebar,
  MessageWrapper,
} from '@web/conversations/Message/MessageLayout/MessageLayout';
import { MessagePreview } from '@web/conversations/Message/MessagePreview/MessagePreview';
import { MessageReactions } from '@web/conversations/Message/MessageReactions/MessageReactions';
import { RetrySendButton } from '@web/conversations/Message/RetrySendButton/RetrySendButton';

import { PostedAt } from './messagePost.styles';

type Props = {
  message: MessagePostType;
  withHeader: boolean;
};
export const MessagePost = ({ message, withHeader }: Props) => {
  const { isOn: isMenuLocked, setOff: unlockMenu, setOn: lockMenu } = useToggle(false);

  const {
    currentUser: {
      settings: { usTimeFormat },
    },
  } = useSession();
  const { isDisabled } = useConversationState();
  const timeFormat = usTimeFormat ? 'h:mm aa' : 'H:mm';
  const editedDateLabel = message.isEdited ? dateToTimeDay(message.editedAt, usTimeFormat) : null;

  const hasAttachments = message.attachments.length > 0;
  const hasContent = message.content.length > 0;

  return (
    <>
      {withHeader && <MessageAuthor author={message.author} />}

      <MessageLayout
        hasError={message.hasFailedToSubmit}
        isActive={isMenuLocked}
        isInteractive={!message.hasFailedToSubmit}>
        <MessageWrapper>
          {message.parentMessage && <MessagePreview message={message.parentMessage} />}
          {hasContent && (
            <MessageContent content={message.content} editedDateLabel={editedDateLabel} />
          )}

          {hasAttachments && (
            <>
              <AttachmentsPreview attachments={message.attachments} />
              <MediaEmbed items={message.attachments} />
            </>
          )}

          <MessageReactions messageId={message.id} />
        </MessageWrapper>

        <MessageSidebar>
          {message.hasFailedToSubmit ? (
            <RetrySendButton message={message} />
          ) : (
            <>
              <PostedAt>{format(message.postedAt, timeFormat)}</PostedAt>

              <MessageContextMenuWrapper>
                <MessageContextMenu
                  isRestricted={isDisabled}
                  lockMenu={lockMenu}
                  message={message}
                  unlockMenu={unlockMenu}
                />
              </MessageContextMenuWrapper>
            </>
          )}
        </MessageSidebar>
      </MessageLayout>
    </>
  );
};
